import React from 'react'
import { t } from 'i18next'
import { Link } from 'react-router-dom'
import {
  Action,
  any,
  async,
  AsyncState,
  IOWidget,
  loadingAsyncState,
  mk_widget,
  Pair,
  stateful
} from 'widgets-for-react'
import { TemplateState } from '../../../../cms/page/types.gen'
import { CMSState, ImageSize, ImageSize as PublicSiteImageSize } from '../../../../cms/blocks/types.gen'
import { CustomBlock } from '../../../../wordpress/types_and_parser/custom_blocks.gen'
import { ExtraImages } from '../../../../public_site/utils/extra_images.mod'
import { getClassName, link, linkWrapper } from '../../../../cms/blocks/utils.gen'

import { Acf } from '../../../../wordpress/types_and_parser/types.mod'

import { Block } from '../../../../wordpress/types_and_parser/standard_blocks.gen'
import { getNewsArticles, getNewsArticle, WordPress, getHighlight } from '../../../../wordpress/wordpress_api'
import { PublicSite_CustomPageData } from '../../../application.mod'
import { moment } from '../../../utils/moment'
import { CustomBlockData } from '../types.mod'
import { ImageContainer } from '../../../../cms/blocks/widgets/ImageContainer.gen'
import { loadedMedia } from '../../../../wordpress/rendering/image_utils.mod'

export type BSLArticleHighlightBlockProps<extra_images> = {
  article_left: WordPress.PostTypeResponse
  articles_right: WordPress.PostTypeResponse[]
  get_default_data_src: () => PublicSiteImageSize<extra_images>
  get_default_data_srcset: () => Pair<PublicSiteImageSize<extra_images>, string>[]
  image_size: ImageSize<extra_images>
  list_image_size: ImageSize<extra_images>
}

type BSLArticleHighlightBlockState<extra_images> = {
  article_left: AsyncState<WordPress.NewsArticleResponse | WordPress.CaseResponse>
  articles_right: AsyncState<WordPress.NewsArticleResponse[]>
}

export type BSLArticleHighlightBlockStyle = Partial<{
  block_classname: string
  inner_classname: string
  left_item_classname: string
  left_item_wrap_classname: string
  left_item_content_classname: string
  left_item_header_classname: string
  left_item_label_classname: string
  left_item_title_classname: string
  left_item_intro_classname: string
  left_author_classname: string
  left_author_info_classname: string
  left_author_name_classname: string
  left_author_function_classname: string
  left_author_visual_classname: string
  left_author_image_classname: string
  left_item_footer_classname: string
  left_item_link_classname: string
  left_item_image_classname: string
  right_list_classname: string
  right_item_wrap_classname: string
  right_item_classname: string
  right_item_link_classname: string
  right_item_content_classname: string
  right_item_header_classname: string
  right_item_label_classname: string
  right_item_title_classname: string
  right_item_visual_classname: string
  right_item_image_classname: string
}>

export function BSLArticleHighlightBlock<customBlockData, extra_images>(
  props: BSLArticleHighlightBlockProps<extra_images>,
  options?: { style?: BSLArticleHighlightBlockStyle; key?: string }
): IOWidget<
  TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>,
  Action<TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>>
> {
  return (s: TemplateState<CustomBlockData, CustomBlock, Block, extra_images, PublicSite_CustomPageData>) =>
    stateful<BSLArticleHighlightBlockState<extra_images>>()(s0 =>
      any<Action<BSLArticleHighlightBlockState<extra_images>>>()([
        async<WordPress.NewsArticleResponse[]>()(s0.articles_right).map(a => s1 => {
          const s2 = a(s1.articles_right)
          return {
            ...s1,
            articles_right: s2
          }
        }),
        async<WordPress.NewsArticleResponse | WordPress.CaseResponse>()(s0.article_left).map(a => s1 => {
          const s2 = a(s1.article_left)
          return {
            ...s1,
            article_left: s2
          }
        }),

        mk_widget({
          run: setState => {
            if (
              s0.article_left.kind == 'loaded' &&
              s0.articles_right.kind == 'loaded' &&
              s0.articles_right.value.length <= 0
            ) {
              return <></>
            }
            if (s0.article_left.kind != 'loaded' || s0.articles_right.kind != 'loaded') {
              return <></>
            }
            return (
              <section className={getClassName(options, 'block_classname', 'container')}>
                <div className={getClassName(options, 'inner_classname', '')}>
                  {s0.article_left.kind === 'loaded' && s0.article_left.value.type ? (
                    <div className={getClassName(options, 'left_item_wrap_classname', '')}>
                      <article className={getClassName(options, 'left_item_classname', '')}>
                        <div className={getClassName(options, 'left_item_content_classname', '')}>
                          <header className={getClassName(options, 'left_item_header_classname', '')}>
                            <span className={getClassName(options, 'left_item_label_classname', '')}>
                              {s0.article_left.value.type == 'news_article' ? 'Nieuws' : 'Case'}
                            </span>
                            <h2 className={getClassName(options, 'left_item_title_classname', '')}>
                              {s0.article_left.value.title.rendered}
                            </h2>
                            {s0.article_left.value.acf.hero_intro && (
                              <div
                                className={getClassName(options, 'left_item_intro_classname', '')}
                                dangerouslySetInnerHTML={{ __html: s0.article_left.value.acf.hero_intro }}
                              ></div>
                            )}
                          </header>
                          {s0.article_left.value.acf.author_name &&
                          s0.article_left.value.acf.author_job &&
                          s0.article_left.value.acf.author_image &&
                          s0.article_left.value.acf.author_image.sizes.super_grid ? (
                            <div className={getClassName(options, 'left_author_classname', '')}>
                              <div className={getClassName(options, 'left_author_info_classname', '')}>
                                {s0.article_left.value.acf.author_name && (
                                  <span className={getClassName(options, 'left_author_name_classname', '')}>
                                    {s0.article_left.value.acf.author_name}
                                  </span>
                                )}
                                {s0.article_left.value.acf.author_job && (
                                  <span className={getClassName(options, 'left_author_function_classname', '')}>
                                    {s0.article_left.value.acf.author_job}
                                  </span>
                                )}
                              </div>
                              {s0.article_left.value.acf.author_image &&
                                s0.article_left.value.acf.author_image.sizes.super_grid && (
                                  <div className={getClassName(options, 'left_author_visual_classname', '')}>
                                    <img
                                      className={getClassName(options, 'left_author_image_classname', '')}
                                      src={s0.article_left.value.acf.author_image.sizes.super_grid}
                                      alt={
                                        s0.article_left.value.acf.author_image.alt
                                          ? s0.article_left.value.acf.author_image.alt
                                          : 'author picture'
                                      }
                                    />
                                  </div>
                                )}
                            </div>
                          ) : (
                            ''
                          )}
                          {s0.article_left.value.link && (
                            <footer className={getClassName(options, 'left_item_footer_classname', '')}>
                              <a
                                className={getClassName(options, 'left_item_link_classname', '')}
                                href={s0.article_left.value.link}
                              >
                                Lees meer
                              </a>
                            </footer>
                          )}
                        </div>
                        {s0.article_left.kind == 'loaded' && s0.article_left.value.acf.image && (
                          ImageContainer(
                            loadedMedia(s0.article_left.value.acf.image.sizes) as AsyncState<
                              Immutable.Map<ImageSize<extra_images>, string>
                            >,
                            props.image_size,
                            getClassName(options, 'left_item_image_classname', 'w-100'),
                            s0.article_left.value.acf.image_alt,
                            props.get_default_data_src,
                            props.get_default_data_srcset
                          ).run(_ => {})
                        )}
                      </article>
                    </div>
                  ) : (
                    ''
                  )}
                  {s0.articles_right.kind === 'loaded' ? (
                    <ul className={getClassName(options, 'right_list_classname', '')}>
                      {s0.articles_right.value.map(content => {
                        return (
                          <li className={getClassName(options, 'right_item_wrap_classname', '')}>
                            <article className={getClassName(options, 'right_item_classname', '')}>
                              <a className={getClassName(options, 'right_item_link_classname', '')} href={content.link}>
                                <div className={getClassName(options, 'right_item_content_classname', '')}>
                                  <header className={getClassName(options, 'right_item_header_classname', '')}>
                                    <span className={getClassName(options, 'right_item_label_classname', '')}>
                                      {content.type == 'news_article' ? 'Nieuws' : 'Case'}
                                    </span>
                                    <h2 className={getClassName(options, 'right_item_title_classname', '')}>
                                      {content.title.rendered}
                                    </h2>
                                  </header>
                                </div>
                                {content.acf.image && (
                                  <div className={getClassName(options, 'right_item_visual_classname', '')}>
                                    {ImageContainer(
                                      loadedMedia(content.acf.image.sizes) as AsyncState<
                                        Immutable.Map<ImageSize<extra_images>, string>
                                      >,
                                      props.list_image_size,
                                      getClassName(options, 'right_item_image_classname', 'w-50'),
                                      content.acf.image_alt,
                                      props.get_default_data_src,
                                      props.get_default_data_srcset
                                    ).run(_ => {})}
                                  </div>
                                )}
                              </a>
                            </article>
                          </li>
                        )
                      })}
                    </ul>
                  ) : (
                    <div>
                      <div className="svg-loader svg-loader--nobg">
                        <svg viewBox="0 0 40 40" className="svg-loader__svg">
                          <path
                            fill="#ccc"
                            className="svg-loader__background"
                            d="M20.201,5.169c-8.254,0-14.946,6.692-14.946,14.946c0,8.255,6.692,14.946,14.946,14.946
                                  s14.946-6.691,14.946-14.946C35.146,11.861,28.455,5.169,20.201,5.169z M20.201,31.749c-6.425,0-11.634-5.208-11.634-11.634
                                  c0-6.425,5.209-11.634,11.634-11.634c6.425,0,11.633,5.209,11.633,11.634C31.834,26.541,26.626,31.749,20.201,31.749z"
                          />
                          <path
                            fill="#000"
                            className="svg-loader__foreground"
                            d="M26.013,10.047l1.654-2.866c-2.198-1.272-4.743-2.012-7.466-2.012h0v3.312h0 C22.32,8.481,24.301,9.057,26.013,10.047z"
                          >
                            <animateTransform
                              attributeType="xml"
                              attributeName="transform"
                              type="rotate"
                              from="0 20 20"
                              to="360 20 20"
                              dur="0.9s"
                              repeatCount="indefinite"
                            />
                          </path>
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )
          }
        })
      ]).map(a => a(s0))
    )({
      article_left: loadingAsyncState(() =>
        getHighlight((props.article_left as any).ID, (props.article_left as any).post_type)
      ),
      articles_right: loadingAsyncState(() => getNewsArticle(props.articles_right))
    }).map(a => s => s)
}
